/* eslint-disable import/no-named-as-default */
import React, { FunctionComponent, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import portalLogo from "../../shared/general/img/hhPortalLogo.png";
import premiumLogo from "../../shared/general/img/hhPremiumLogo.png";
import logoPink from "../../shared/general/img/logoPink.png";
import Pipeline from "../EmployerPerformance/Pipeline";
import TimeToHire from "../EmployerPerformance/TimeToHire";
import OfferToPlacement from "../EmployerPerformance/OfferToPlacement";
import TotalJobsPosted from "../EmployerPerformance/TotalJobsPosted";
import SpendPerSectorPdfExport from "./SpendPerSectorPdfExport";
import TotalJobsPerSectorPdfExport from "./TotalJobsPerSectorPdfExport";
import TimeToHireTimeline from "../EmployerPerformance/TimeToHireTimeline";
import ApplicationActionsTracking from "../EmployerPerformance/ApplicationActionsTracking";
import pdfExportOptionsTypes from "../types/pdfExportOptionsTypes";
import { ChartGroup } from "../styles/employer-analytics-styles";
import {
  Wrapper,
  Inner,
  PortalLogo,
  PremiumLogo,
  HhLogo,
  Charts,
  NoneSelected,
  Footer,
  FooterText,
  FooterTime,
} from "../styles/pdf-export-page-styles";

interface IOptionItemProps {
  id: number;
  value: string;
  isChecked: boolean;
  label: string;
}

export interface IEmployerExportPageProps {
  preview?: boolean;
  isPortalUser: boolean;
  isPremiumUser: boolean;
  options: Array<IOptionItemProps>;
  noneSelected?: boolean;
}

export const EmployerPerformancePdfExportPage: FunctionComponent<
  IEmployerExportPageProps
> = ({ preview, options, isPortalUser, isPremiumUser, noneSelected }) => {
  const date: Date = new Date();
  const dateToDisplay = `${date.toLocaleString("default", {
    month: "long",
  })} ${date.getDate()}, ${date.getFullYear()} | ${date.getHours()}:${
    date.getMinutes() < 10 ? "0" : ""
  }${date.getMinutes()}`;

  const pipeline = options[0];
  const timeToHire = options[1];
  const offerToPlacement = options[2];
  const spendPerSector = options[3];
  const totalJobsPosted = options[4];
  const totalJobsPerSector = options[5];
  const timeToHireTimeline = options[6];
  const applicationActionsTracking = options[7];

  return (
    <Wrapper
      preview={preview}
      id={preview ? "employer-performance-preview-page" : "pdf-export-page"}
    >
      {isPortalUser && (
        <PortalLogo
          src={portalLogo as string}
          alt="Portal logo"
          noneSelected={noneSelected}
        />
      )}
      {isPremiumUser && (
        <PremiumLogo
          src={premiumLogo as string}
          alt="HH Premium logo"
          noneSelected={noneSelected}
        />
      )}
      {!isPortalUser && !isPremiumUser && (
        <HhLogo
          src={logoPink as string}
          alt="HH logo"
          noneSelected={noneSelected}
        />
      )}
      <Inner>
        {noneSelected ? (
          <NoneSelected>
            Please select at least one chart to export
          </NoneSelected>
        ) : (
          <Fragment>
            <Charts>
              {pipeline.isChecked && <Pipeline pdfExport />}
              {(timeToHire.isChecked || offerToPlacement.isChecked) && (
                <ChartGroup pdfExport>
                  {timeToHire.isChecked && (
                    <TimeToHire
                      pdfExport
                      pipelineSelected={pipeline.isChecked}
                    />
                  )}
                  {offerToPlacement.isChecked && (
                    <OfferToPlacement
                      pdfExport
                      pipelineSelected={pipeline.isChecked}
                    />
                  )}
                </ChartGroup>
              )}
            </Charts>
            <Charts
              height={(
                (spendPerSector.isChecked || totalJobsPosted.isChecked) &&
                320
              ).toString()}
            >
              {spendPerSector.isChecked && <SpendPerSectorPdfExport />}
              {totalJobsPosted.isChecked && (
                <TotalJobsPosted
                  pdfExport
                  spendPerSectorPdfExportSelected={spendPerSector.isChecked}
                />
              )}
            </Charts>
            <Charts
              height={(
                (totalJobsPerSector.isChecked ||
                  timeToHireTimeline.isChecked) &&
                325
              ).toString()}
            >
              {totalJobsPerSector.isChecked && <TotalJobsPerSectorPdfExport />}
              {timeToHireTimeline.isChecked && (
                <TimeToHireTimeline
                  pdfExport
                  totalJobsPerSectorSelected={totalJobsPerSector.isChecked}
                />
              )}
            </Charts>
            <Charts
              height={(applicationActionsTracking.isChecked && 330).toString()}
            >
              {applicationActionsTracking.isChecked && (
                <ApplicationActionsTracking pdfExport />
              )}
            </Charts>
          </Fragment>
        )}
      </Inner>
      <Footer>
        {isPortalUser && (
          <FooterText>
            Portal by <strong>Hiring Hub</strong>
          </FooterText>
        )}
        {isPremiumUser && (
          <FooterText>
            <strong>Hiring Hub</strong> Premium
          </FooterText>
        )}
        {!isPortalUser && !isPremiumUser && (
          <FooterText>
            <strong>Hiring Hub</strong>
          </FooterText>
        )}
        <FooterTime>{dateToDisplay}</FooterTime>
      </Footer>
    </Wrapper>
  );
};

EmployerPerformancePdfExportPage.propTypes = {
  preview: PropTypes.bool,
  options: pdfExportOptionsTypes.isRequired,
  isPortalUser: PropTypes.bool.isRequired,
  isPremiumUser: PropTypes.bool.isRequired,
  noneSelected: PropTypes.bool,
};

EmployerPerformancePdfExportPage.defaultProps = {
  preview: null,
  noneSelected: null,
};

interface IReduxState {
  employerAnalytics: {
    pdfExportOptions: {
      options: Array<IOptionItemProps>;
    };
    isPortalUser: boolean;
    isPremiumUser: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  options: state.employerAnalytics.pdfExportOptions.options,
  isPortalUser: state.employerAnalytics.isPortalUser,
  isPremiumUser: state.employerAnalytics.isPremiumUser,
});

export default connect(mapStateToProps, {})(EmployerPerformancePdfExportPage);
